import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, STATUS_META, STATUS_ORDER, CHECKLIST_META, CHECKLIST_VALUES } from "@/lib/api";
import { ExpectedDelivery } from "@/components/ExpectedDelivery";
import { useFeatures } from "@/lib/features";
import { StatusBadge, StatusTimeline } from "@/components/StatusBadge";
import { BudgetSection } from "@/components/BudgetSection";
import { FiscalPanel } from "@/components/fiscal/FiscalPanel";
import { OrderQrCard } from "@/components/OrderQrCard";
import { InspectionCard } from "@/components/InspectionCard";
import { TechChecklistCard, TechChecklistView } from "@/components/TechChecklist";
import { OilChangeCard } from "@/components/OilChangeCard";
import { OutsourcedCard } from "@/components/Outsourced";
import { LossButton } from "@/components/Losses";
import { OrderFlags } from "@/components/OrderFlags";
import { OrderTimers } from "@/components/OrderTimers";
import { SurveyButton } from "@/components/Labor";
import { printPdfBlob, preopenForIOS } from "@/lib/print";
import { uploadPhotos } from "@/lib/draft";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Printer, ArrowLeft, Camera, Loader2, Save, Trash2, X, Car, User, CalendarClock, History, Send, ShieldCheck, PackageCheck, MessageCircle, Video, FileText, Download, Share2, Wallet, Pencil, Star } from "lucide-react";
import { ReceiptButtons } from "@/components/ReceiptButtons";
import { PaymentDialog } from "@/components/PaymentDialog";
import { PaymentStatusControl, PaymentStatusBadge } from "@/components/PaymentStatusPill";
import { ProfitBoard } from "@/components/ProfitBoard";
import { WaLogCard } from "@/components/WaLogCard";
import { PaymentsBlock } from "@/components/PaymentsBlock";
import { OrderWorkInfo } from "@/components/OrderWorkInfo";
import { LaudoSection } from "@/components/LaudoSection";
import { brl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useWaMsg, waUrl, vehicleShort, budgetPdfMsg, intakePdfMsg } from "@/lib/waTemplates";

function VideoPlayer({ video, onRemove }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/videos/${video.id}`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [video.id]);
  return (
    <div data-testid={`video-item-${video.id}`} className="relative group rounded-xl overflow-hidden border border-card2-border bg-black">
      {url ? (
        <video
          data-testid={`video-player-${video.id}`}
          src={url}
          controls
          playsInline
          preload="metadata"
          className="w-full aspect-video object-contain bg-black"
        />
      ) : (
        <div className="w-full aspect-video flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-zinc-600 animate-spin" />
        </div>
      )}
      {onRemove && (
        <button
          data-testid={`video-remove-${video.id}`}
          onClick={onRemove}
          className="absolute top-1.5 right-1.5 h-8 w-8 rounded-full bg-black/70 border border-zinc-700 text-zinc-200 opacity-100 flex items-center justify-center"
          aria-label="Remover vídeo"
          title="Remover vídeo"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function PhotoThumb({ photoId, endpoint, onClick, alt = "Foto da ordem" }) {
  const [url, setUrl] = useState("");
  const src = endpoint || `/files/${photoId}`;
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(src, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [src]);
  if (!url) return <div className="aspect-square rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center"><Loader2 className="h-5 w-5 text-zinc-600 animate-spin" /></div>;
  return (
    <button type="button" onClick={() => onClick(url)} className="group relative aspect-square overflow-hidden rounded-lg border border-zinc-800 hover:border-green-brand/60 transition-colors">
      <img src={url} alt={alt} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
    </button>
  );
}

function RatingComment({ order, saving, onSave }) {
  const [t, setT] = useState(order.customer_rating_comment || "");
  useEffect(() => { setT(order.customer_rating_comment || ""); }, [order.customer_rating_comment]);
  const dirty = (t || "").trim() !== (order.customer_rating_comment || "");
  return (
    <div className="mt-3">
      <label className="text-[11px] font-mono-caps uppercase text-zinc-500">Comentário</label>
      <Textarea data-testid="customer-rating-comment" value={t} onChange={(e) => setT(e.target.value)} rows={2} placeholder="O que o cliente disse sobre o atendimento" className="mt-1 bg-zinc-900 border-zinc-800" />
      {dirty && <Button data-testid="customer-rating-comment-save" size="sm" disabled={saving} onClick={() => onSave(t)} className="mt-2 bg-amber-brand hover:bg-amber-brandDark text-black font-bold">Salvar comentário</Button>}
    </div>
  );
}


export default function OrderDetail({ readOnly = false }) {
  const budgetDraftRef = useRef(null);
  const [liveBudgetItems, setLiveBudgetItems] = useState(null);
  const [liveBudgetTotal, setLiveBudgetTotal] = useState(null);
  const features = useFeatures();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const waMsg = useWaMsg();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingIntake, setUploadingIntake] = useState(false);
  const [uploadingExit, setUploadingExit] = useState(false);
  const [sendingRev, setSendingRev] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [preview, setPreview] = useState("");
  const fileRef = useRef(null);
  const intakeRef = useRef(null);
  const exitRef = useRef(null);
  const videoRef = useRef(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [workSaving, setWorkSaving] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const location = useLocation();

  const basePath = user?.role === "customer" ? "/cliente" : "/owner";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate(basePath, { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, basePath]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!order || location.hash !== "#orcamento") { return; }
    setTimeout(() => document.querySelector("[data-testid=budget-owner-view],[data-testid=budget-view],[data-testid=budget-empty],[data-testid^=budget-]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [!!order, location.hash]); // eslint-disable-line react-hooks/exhaustive-deps
  // Silent refresh: keeps the page mounted so unsaved budget/inspection edits are not lost.
  const refresh = useCallback(async () => {
    try { const { data } = await api.get(`/orders/${id}`); setOrder(data); } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  const [failedUploads, setFailedUploads] = useState({});
  const runUpload = async (category, files, field, setBusy, ref) => {
    setBusy(true);
    let ok = 0;
    const failed = await uploadPhotos(id, files, category, (data) => { ok += 1; setOrder((prev) => (prev ? { ...prev, [field]: [...(prev[field] || []), data] } : prev)); });
    setFailedUploads((p) => ({ ...p, [category]: failed }));
    if (ok) toast.success(ok > 1 ? `${ok} fotos enviadas` : "Foto enviada");
    if (failed.length) toast.error(`${failed.length} foto(s) não subiram. Toque em "Tentar de novo".`);
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  const mergeServerOrder = (prev, data) => {
    if (!prev) return data;
    // Keep user's unsaved edits for fields the owner is actively editing.
    return {
      ...data,
      checklist: prev.checklist,
      mechanic_notes: prev.mechanic_notes,
      technical_report: prev.technical_report,
      next_revision: prev.next_revision,
      vehicle: prev.vehicle,
      photos: prev.photos,
      intake_photos: prev.intake_photos,
    };
  };

  const patch = async (updates) => {
    if (updates.status === "em_andamento" && !order?.mechanic_name && !updates.mechanic_name) {
      const sug = (order?.shop_mechanics || []).map((m) => m.name).filter(Boolean);
      const name = (window.prompt("Escolha o mecânico responsável antes de iniciar o atendimento:" + (sug.length ? `\nSugestões: ${sug.join(", ")}` : ""), "") || "").trim();
      if (!name) { toast.info("É preciso informar o mecânico responsável"); return; }
      updates = { ...updates, mechanic_name: name };
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${id}`, updates);
      setOrder((prev) => mergeServerOrder(prev, data));
      toast.success("Alterações salvas");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const flushBudget = async () => {
    const r = budgetDraftRef.current ? await budgetDraftRef.current() : true;
    if (r === "saved") setPdfBlobs({});
    return r;
  };

  const saveAll = async () => {
    if (!order) return;
    if ((await flushBudget()) === false) return;
    const nr = order.next_revision || {};
    const hasNr = (nr.date != null && nr.date !== "") || (nr.km != null && nr.km !== "") || (nr.note != null && nr.note !== "");
    const payload = {
      checklist: order.checklist,
      mechanic_notes: order.mechanic_notes || "",
      technical_report: order.technical_report || "",
    };
    if (hasNr) {
      payload.next_revision = {
        date: nr.date || null,
        km: nr.km != null && nr.km !== "" ? Number(nr.km) : null,
        note: nr.note || null,
      };
    }
    if (order.vehicle?.current_km != null && order.vehicle.current_km !== "") {
      payload.current_km = Number(order.vehicle.current_km);
    }
    setSavingAll(true);
    try {
      const { data } = await api.patch(`/orders/${id}`, payload);
      setOrder((prev) => ({
        ...data,
        photos: prev?.photos || data.photos,
        intake_photos: prev?.intake_photos || data.intake_photos,
      }));
      toast.success("Alterações salvas");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAll(false);
    }
  };

  const handleBudgetUpdate = (data) => {
    setOrder((prev) => mergeServerOrder(prev, data));
  };

  const setChecklistValue = (key, value) => {
    const next = order.checklist.map((c) => (c.key === key ? { ...c, value } : c));
    setOrder({ ...order, checklist: next });
  };

  const saveDescription = async () => {
    const next = (descDraft || "").trim();
    if (next === (order.description || "").trim()) { setEditingDesc(false); return; }
    setSavingDesc(true);
    try {
      const data = await patch({ description: next });
      if (data) setOrder((prev) => ({ ...(prev || data), description: data.description, description_history: data.description_history }));
      setEditingDesc(false);
    } finally { setSavingDesc(false); }
  };

  const onUpload = (e) => { const f = Array.from(e.target.files || []); if (f.length) runUpload("service", f, "photos", setUploading, fileRef); };
  const onUploadIntake = (e) => { const f = Array.from(e.target.files || []); if (f.length) runUpload("intake", f, "intake_photos", setUploadingIntake, intakeRef); };
  const onUploadExit = (e) => { const f = Array.from(e.target.files || []); if (f.length) runUpload("exit", f, "exit_photos", setUploadingExit, exitRef); };
  const RETRY = { service: ["photos", setUploading, fileRef], intake: ["intake_photos", setUploadingIntake, intakeRef], exit: ["exit_photos", setUploadingExit, exitRef] };
  const RetryUploads = ({ category }) => (failedUploads[category]?.length ? (
    <button type="button" data-testid={`retry-upload-${category}`} onClick={() => runUpload(category, failedUploads[category], ...RETRY[category])}
      className="mt-2 inline-flex items-center gap-1 rounded-md border border-red-700 bg-red-950/40 px-3 h-9 text-xs font-bold text-red-200">
      {failedUploads[category].length} foto(s) não enviada(s) — Tentar de novo
    </button>
  ) : null);

  const readDuration = (file) =>
    new Promise((resolve, reject) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); resolve(d); };
      v.onerror = () => { URL.revokeObjectURL(v.src); reject(new Error("Arquivo de vídeo inválido")); };
      v.src = URL.createObjectURL(file);
    });

  const onUploadVideo = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const f = files[0];
    const existingCount = (order?.service_videos || []).length;
    if (existingCount >= 3) {
      toast.error("Limite de 3 vídeos por ordem");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("O vídeo passa de 50 MB. Grave um mais curto ou com menos qualidade.");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    try {
      const dur = await readDuration(f);
      if (dur > 30.5) {
        toast.error(`Esse vídeo tem ${dur.toFixed(1)}s. O limite é de 30 segundos.`);
        if (videoRef.current) videoRef.current.value = "";
        return;
      }
    } catch {
      toast.error("Não foi possível ler o vídeo. Use .mp4 ou .mov.");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    setUploadingVideo(true); setVideoProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/orders/${id}/videos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => { if (ev.total) setVideoProgress(Math.round((ev.loaded * 100) / ev.total)); },
      });
      setOrder((prev) => (prev ? { ...prev, service_videos: [...(prev.service_videos || []), data] } : prev));
      toast.success("Vídeo enviado");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingVideo(false);
      setVideoProgress(0);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const removeVideo = async (vid) => {
    try {
      await api.delete(`/orders/${id}/videos/${vid}`);
      setOrder((prev) => (prev ? { ...prev, service_videos: (prev.service_videos || []).filter((v) => v.id !== vid) } : prev));
      toast.success("Vídeo removido");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.delete(`/orders/${id}/photos/${photoId}`);
      setOrder((prev) => prev ? {
        ...prev,
        photos: (prev.photos || []).filter((p) => p.id !== photoId),
        intake_photos: (prev.intake_photos || []).filter((p) => p.id !== photoId),
        exit_photos: (prev.exit_photos || []).filter((p) => p.id !== photoId),
      } : prev);
      toast.success("Foto removida");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const whatsappLink = () => {
    const digits = (order?.customer_phone || "").replace(/\D/g, "");
    if (!digits) return "";
    // 10-11 digits = DDD + número → prefix +55. 12-13 digits = já tem 55 → mantém.
    const wa = digits.length >= 12 ? digits : `55${digits}`;
    const base = (window?.location?.origin || "").replace(/\/$/, "");
    const url = `${base}/cliente/ordens/${order.id}`;
    const statusLabel = STATUS_META?.[order.status]?.label || order.status || "";
    const vars = { ..._waVars(), link: url, status: statusLabel };
    return waUrl(wa, waMsg(order.status === "pronto" ? "car_ready" : "status_update", vars));
  };

  const _waNumber = () => {
    const digits = (order?.customer_phone || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.length >= 12 ? digits : `55${digits}`;
  };
  const _waAppUrl = () => `${(window?.location?.origin || "").replace(/\/$/, "")}/cliente/ordens/${order.id}`;
  const _waVars = () => ({
    cliente: order.customer_name || "", placa: order?.vehicle?.plate || "",
    modelo: vehicleShort(order?.vehicle),
    valor: order?.budget ? `R$ ${Number(order.budget.total || 0).toFixed(2).replace(".", ",")}` : "",
  });

  const whatsappLinkBudget = () => {
    const wa = _waNumber();
    if (!wa) return "";
    return waUrl(wa, budgetPdfMsg(_waVars(), budgetPdfPublicUrl));
  };

  const whatsappLinkIntake = () => {
    const wa = _waNumber();
    if (!wa) return "";
    return waUrl(wa, intakePdfMsg(_waVars(), intakePdfPublicUrl));
  };

  const whatsappLinkRevision = () => {
    const wa = _waNumber();
    if (!wa) return "";
    const nrLocal = order?.next_revision || {};
    const plate = order?.vehicle?.plate || "";
    const parts = [];
    if (nrLocal.date) {
      try {
        const d = new Date(nrLocal.date + "T00:00:00").toLocaleDateString("pt-BR");
        parts.push(`em ${d}`);
      } catch {}
    }
    if (nrLocal.km != null && nrLocal.km !== "") {
      parts.push(`aos ${Number(nrLocal.km).toLocaleString("pt-BR")} km`);
    }
    const when = parts.length ? parts.join(" ou ").replace(/^em /, "") : "breve";
    return waUrl(wa, waMsg("revision", { ..._waVars(), placa: plate, data: when, link: _waAppUrl() }));
  };

  const [budgetPdfPublicUrl, setBudgetPdfPublicUrl] = useState("");
  const [fullPdfPublicUrl, setFullPdfPublicUrl] = useState("");
  const [intakePdfPublicUrl, setIntakePdfPublicUrl] = useState("");
  const [pdfBlobs, setPdfBlobs] = useState({ budget: null, full: null });
  const [pdfLoading, setPdfLoading] = useState({ budget: false, full: false });

  useEffect(() => {
    if (!order?.id || readOnly) return;
    let cancelled = false;
    (async () => {
      for (const kind of ["budget", "full", "intake"]) {
        try {
          const { data } = await api.get(`/orders/${order.id}/pdf/token?kind=${kind}`);
          if (cancelled || !data?.path) continue;
          const url = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
          if (kind === "budget") setBudgetPdfPublicUrl(url);
          else if (kind === "intake") setIntakePdfPublicUrl(url);
          else setFullPdfPublicUrl(url);
        } catch {}
      }
    })();
    return () => { cancelled = true; };
  }, [order?.id, readOnly]);

  // Prefetch PDF blobs so the share sheet opens under the user's tap (keeps iOS user activation).
  useEffect(() => {
    if (!order?.id) return;
    let cancelled = false;
    (async () => {
      for (const kind of ["budget", "full"]) {
        setPdfLoading((p) => ({ ...p, [kind]: true }));
        try {
          const resp = await api.get(`/orders/${order.id}/pdf/${kind}`, { responseType: "blob" });
          if (!cancelled) setPdfBlobs((p) => ({ ...p, [kind]: resp.data }));
        } catch {} finally {
          if (!cancelled) setPdfLoading((p) => ({ ...p, [kind]: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [order?.id, order?.updated_at]);

  const _pdfFilename = (kind) => {
    const num = order?.order_number ?? (order?.id || "").slice(-6);
    const plate = (order?.vehicle?.plate || "OS").replace(/[\/]/g, "-");
    const suf = kind === "budget" ? "orcamento" : "os-completa";
    return `OS-${num}-${plate}-${suf}.pdf`;
  };

  const _fetchPdfBlob = async (kind) => {
    const resp = await api.get(`/orders/${order.id}/pdf/${kind}`, { responseType: "blob" });
    return resp.data;
  };

  const printPdf = async (kind) => {
    const win = preopenForIOS();
    const fl = await flushBudget();
    if (fl === false) { if (win) win.close(); return; }
    if (fl !== "saved" && pdfBlobs[kind]) { if (win) win.close(); printPdfBlob(pdfBlobs[kind]); return; }
    try {
      const resp = await api.get(`/orders/${order.id}/pdf/${kind}`, { responseType: "blob" });
      setPdfBlobs((p) => ({ ...p, [kind]: resp.data }));
      printPdfBlob(resp.data, win);
    } catch (e) { if (win) win.close(); toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (kind) => {
    if ((await flushBudget()) === false) return;
    try {
      const blob = await _fetchPdfBlob(kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = _pdfFilename(kind); document.body.appendChild(a); a.click();
      a.remove(); setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sharePdf = async (kind) => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
    if (!isMobile) { await downloadPdf(kind); return; }
    const fl = await flushBudget();
    if (fl === false) return;
    const title = `OS #${order.order_number ?? ""} ${order?.vehicle?.plate || ""}`.trim();
    const text = kind === "budget" ? `Or\u00e7amento da ${title}` : `OS completa — ${title}`;
    const blob = fl === "saved" ? null : pdfBlobs[kind];
    if (blob && navigator.canShare) {
      const file = new File([blob], _pdfFilename(kind), { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title, text });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }
    }
    // Not ready yet — fetch now so a second tap can share with user activation.
    if (!blob) {
      setPdfLoading((p) => ({ ...p, [kind]: true }));
      try {
        const b = await _fetchPdfBlob(kind);
        setPdfBlobs((p) => ({ ...p, [kind]: b }));
        toast.info("PDF pronto. Toque em Enviar para compartilhar.");
        return;
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setPdfLoading((p) => ({ ...p, [kind]: false }));
      }
    }
    // Browser can't share files — open WhatsApp with the 72h public link instead.
    const publicUrl = kind === "budget" ? budgetPdfPublicUrl : fullPdfPublicUrl;
    if (publicUrl) {
      const msg = `${text}\n${publicUrl}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
      return;
    }
    toast.info("Compartilhamento indisponível neste navegador. Use o botão Baixar.");
  };

  const removeOrder = async () => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Ordem excluída");
      navigate(basePath, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendRevisionReminder = async () => {
    setSendingRev(true);
    try {
      await api.post(`/orders/${id}/send-revision-reminder`);
      toast.success("Lembrete enviado ao cliente por e-mail");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingRev(false);
    }
  };

  if (loading || !order) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>;
  }

  const nr = order.next_revision || {};
  const plate = order.vehicle?.plate;
  const historyLink = plate
    ? `${basePath}/veiculo/${encodeURIComponent(plate)}`
    : null;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <button
          data-testid="back-btn"
          onClick={() => (location.key && location.key !== "default" ? navigate(-1) : navigate(basePath))}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-green-brand transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        {historyLink && (
          <Link
            data-testid="history-link"
            to={historyLink}
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-brand transition-colors"
          >
            <History className="h-4 w-4" /> Histórico do veículo
          </Link>
        )}
      </div>

      <div className="rounded-2xl border border-card2-border bg-card2">
        <div className="p-5 md:p-7 border-b border-card2-border">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-mono-caps text-zinc-500 flex items-center gap-2">
                <span>Ordem de serviço</span>
                {order.order_number != null && (
                  <span data-testid="os-number" className="inline-flex items-center gap-1 rounded-full border border-green-brand/40 bg-green-brand/10 text-green-neon px-2 py-0.5 text-[11px] font-bold tracking-wider">
                    OS #{order.order_number}
                  </span>
                )}
              </div>
              <h1 data-testid="order-plate" className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">
                <span className="text-zinc-500 text-xl sm:text-2xl mr-2">{order.no_vehicle ? "" : "Placa:"}</span>{order.no_vehicle ? "Serviço sem veículo" : plate || "Sem placa"}
              </h1>
              {order.consultant_name && <div data-testid="order-consultant" className="mt-1 text-sm text-zinc-400">Consultor: <span className="font-semibold text-zinc-200">{order.consultant_name}</span></div>}
              <div className="mt-1 text-sm text-zinc-400 flex items-center gap-2 flex-wrap" translate="no">
                <Car className="h-4 w-4 text-amber-brand" />
                <span>
                  {[
                    order.vehicle?.make,
                    order.vehicle?.model,
                    order.vehicle?.year || order.vehicle?.year_model
                      ? `· ${[order.vehicle.year, order.vehicle.year_model].filter(Boolean).join("/")}`
                      : null,
                    order.vehicle?.color ? `· ${order.vehicle.color}` : null,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              </div>
              {order.vehicle?.current_km != null && (
                <div data-testid="order-vehicle-km" className="mt-1 text-sm text-zinc-300 flex items-center gap-2" translate="no">
                  <span className="text-zinc-500">Quilometragem:</span>
                  <span className="font-semibold">{Number(order.vehicle.current_km).toLocaleString("pt-BR")} km</span>
                </div>
              )}
              {order.vehicle?.chassi && (
                <div data-testid="order-vehicle-chassi" className="mt-1 text-xs text-zinc-500" translate="no">
                  Chassi: <span className="font-mono text-zinc-300">{order.vehicle.chassi}</span>
                </div>
              )}
              {order.customer_name && (
                <div className="mt-1 text-sm text-zinc-400 flex items-center gap-2" translate="no">
                  <User className="h-4 w-4 text-green-brand" />
                  <span>{order.customer_name}</span>
                  <span className="text-zinc-600">·</span>
                  <span className="text-zinc-500">{order.customer_email}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 sm:self-start">
              <StatusBadge status={order.status} />
              {!readOnly && order.customer_phone && (
                <a
                  data-testid="wa-send-btn"
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wide"
                  title="Abrir WhatsApp com mensagem pronta"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
            </div>
          </div>
          {!readOnly && editingDesc ? (
            <div data-testid="order-desc-edit" className="mt-4 space-y-2">
              <Textarea data-testid="order-desc-input" value={descDraft} onChange={(e) => setDescDraft(e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[90px]" placeholder="O que o cliente relatou" />
              <div className="flex gap-2">
                <Button data-testid="order-desc-save" size="sm" onClick={saveDescription} disabled={savingDesc} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">
                  {savingDesc ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salvar
                </Button>
                <Button data-testid="order-desc-cancel" size="sm" variant="outline" onClick={() => { setEditingDesc(false); setDescDraft(order.description || ""); }} className="bg-zinc-950 border-zinc-800">
                  <X className="h-4 w-4 mr-1" /> Cancelar
                </Button>
              </div>
              {(order.description_history || []).length > 0 && (
                <details data-testid="order-desc-history" className="text-[11px] text-zinc-500">
                  <summary className="cursor-pointer hover:text-zinc-300">Histórico de alterações ({order.description_history.length})</summary>
                  <ul className="mt-1 space-y-1">
                    {order.description_history.slice().reverse().map((h, i) => (
                      <li key={i} className="pl-2 border-l border-zinc-800"><span className="text-zinc-400">{new Date(h.at).toLocaleString("pt-BR")}</span>{h.by_name ? ` · ${h.by_name}` : ""}<div className="whitespace-pre-wrap text-zinc-500">{h.previous || <span className="italic">(vazio)</span>}</div></li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ) : (
            <div className="mt-4 flex items-start gap-2">
              <p data-testid="order-description" className="text-zinc-200 whitespace-pre-wrap flex-1">{order.description || <span className="text-zinc-600">Sem reclamação registrada.</span>}</p>
              {!readOnly && (
                <button type="button" data-testid="order-desc-edit-btn" onClick={() => { setDescDraft(order.description || ""); setEditingDesc(true); }} title="Editar reclamação do cliente"
                  className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-md border border-zinc-800 bg-zinc-950 text-zinc-300 hover:text-green-brand hover:border-green-brand">
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <LaudoSection order={order} setOrder={setOrder} readOnly={readOnly} />
          <ExpectedDelivery order={order} readOnly={readOnly} onSave={patch} />
          <div className="mt-5">
            <StatusTimeline status={order.status} />
          </div>
          {features.enabled("fiscal.cupom_termico") && <div className="mt-4 flex justify-end">
            <Button data-testid="order-thermal-receipt" type="button" variant="outline" size="sm" onClick={() => window.open(`/owner/ordens/${order.id}/cupom`, "_blank", "noopener")} className="h-9 bg-zinc-950 border-zinc-800 text-zinc-200 hover:bg-zinc-900">
              <Printer className="h-4 w-4 mr-1" /> Imprimir cupom
            </Button>
          </div>}
          <div data-testid="pdf-actions" className="mt-5 pt-4 border-t border-card2-border grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[{ kind: "budget", label: "PDF do or\u00e7amento" }, { kind: "full", label: "PDF da OS completa" }].map(({ kind, label }) => (
              <div key={kind} className="rounded-xl border border-card2-border bg-appbg-soft/40 p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-green-brand/15 border border-green-brand/40 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-green-neon" />
                </div>
                <div className="flex-1 min-w-0 text-sm text-zinc-100 font-semibold">{label}</div>
                <Button
                  data-testid={`pdf-${kind}-print`}
                  type="button"
                  onClick={() => printPdf(kind)}
                  variant="outline"
                  size="sm"
                  className="h-9 bg-zinc-950 border-zinc-800 text-zinc-200 hover:bg-zinc-900"
                  title="Imprimir PDF"
                >
                  <Printer className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Imprimir</span>
                </Button>
                <Button
                  data-testid={`pdf-${kind}-download`}
                  type="button"
                  onClick={() => downloadPdf(kind)}
                  variant="outline"
                  size="sm"
                  className="h-9 bg-zinc-950 border-zinc-800 text-zinc-200 hover:bg-zinc-900"
                  title="Baixar PDF"
                >
                  <Download className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Baixar</span>
                </Button>
                <Button
                  data-testid={`pdf-${kind}-share`}
                  type="button"
                  onClick={() => sharePdf(kind)}
                  disabled={pdfLoading[kind] && !pdfBlobs[kind]}
                  size="sm"
                  className="h-9 bg-green-brand hover:bg-green-brandDark font-bold uppercase disabled:opacity-60"
                  title="Compartilhar PDF"
                >
                  <Share2 className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">
                    {pdfLoading[kind] && !pdfBlobs[kind] ? "Preparando…" : pdfBlobs[kind] ? "Enviar" : "Compartilhar"}
                  </span>
                </Button>
              </div>
            ))}
          </div>
        </div>

        {!readOnly && (
          <div className="p-5 md:p-7 border-b border-card2-border bg-appbg-soft/60">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <div>
                <div className="text-[11px] font-mono-caps text-zinc-500">Atualizar status</div>
                <Select value={order.status} onValueChange={(v) => {
                  if (v === "pronto" && !order?.payment && user?.role === "owner") {
                    setPendingStatus(v); setPaymentOpen(true); return;
                  }
                  patch({ status: v });
                }}>
                  <SelectTrigger data-testid="status-select" className="mt-2 min-w-[220px] h-11 bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>
                        {STATUS_META[s].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="delete-order-btn" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-950/40">
                    <Trash2 className="h-4 w-4 mr-2" /> Excluir ordem
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir esta ordem?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Esta ação remove a ordem e todas as fotos associadas. Não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancelar</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirm-delete-order" onClick={removeOrder} className="bg-red-700 hover:bg-red-600 text-white">
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            {!readOnly && user?.role === "owner" && (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <OrderWorkInfo order={order} saving={workSaving} onSave={async (payload) => { setWorkSaving(true); try { await patch(payload); } finally { setWorkSaving(false); } }} />
                <div data-testid="order-payment-summary" className="flex flex-col gap-2">
          {!readOnly && features.enabled("os.payments") && <PaymentStatusControl order={order} onChanged={(d) => setOrder((prev) => ({ ...(prev || {}), ...d }))} canEdit={user?.role === "owner"} />}
          {!readOnly && features.enabled("os.payments") && <PaymentsBlock key={order?.payment?.recorded_at || "none"} source="orders" id={order.id} liveTotal={liveBudgetTotal} refreshKey={`${order?.budget?.items?.length || 0}:${order?.budget?.total || 0}:${order?.budget?.total_discount_amount || 0}`} onChanged={refresh} />}
                  {order?.payment?.financier && <div className="text-xs text-zinc-400">Financeira: {order.payment.financier}</div>}
                  {order?.payment?.methods?.length > 0 && <ReceiptButtons source="order" id={order.id} />}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Budget */}
        <div className="p-5 md:p-7 border-b border-card2-border">
          {!readOnly && user?.role === "owner" && order?.budget?.items?.length > 0 && (
            <div className="mb-3"><ProfitBoard order={order} items={liveBudgetItems || undefined} /></div>
          )}
          <BudgetSection order={order} readOnly={readOnly} onUpdated={handleBudgetUpdate} whatsappLink={whatsappLinkBudget} draftRef={budgetDraftRef} onLiveItems={setLiveBudgetItems} onLiveTotal={setLiveBudgetTotal} />
          {!readOnly && user?.role === "owner" && <div className="mt-3"><WaLogCard orderId={order.id} refreshKey={`${order.status}-${order.updated_at}`} /></div>}
        </div>

        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <FiscalPanel order={order} onOrderChanged={refresh} />
          </div>
        )}
        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <OrderQrCard order={order} />
          </div>
        )}
        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <InspectionCard key={`insp-${order.id}`} order={order} onSaved={refresh} />
          </div>
        )}
        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            {order.checklist_pending && <div data-testid="order-checklist-pending" className="mb-3 rounded-lg border border-violet-600/60 bg-violet-950/30 px-3 py-2 text-sm text-violet-200">Checklist pendente — preencha o checklist completo abaixo. A etiqueta some quando salvar.</div>}
            <TechChecklistCard key={`tc-${order.id}`} order={order} onUpdated={handleBudgetUpdate} />
          </div>
        )}
        {readOnly && order.tech_checklist?.entries?.some((e) => e.state) && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <div className="text-[11px] font-mono-caps text-zinc-500 mb-2">Checklist completo (inspeção técnica)</div>
            <TechChecklistView entries={order.tech_checklist.entries} />
          </div>
        )}
        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <div className="mb-4"><OrderTimers order={order} readOnly={readOnly} /></div>
            <OutsourcedCard order={order} />
            <div className="mt-4"><LossButton order={order} /></div>
          </div>
        )}
        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <OrderFlags key={`flags-${order.id}`} order={order} onChange={(d) => setOrder((o) => ({ ...o, ...d }))} />
          </div>
        )}
        {!readOnly && user?.role === "owner" && (
          <div className="p-5 md:p-7 border-b border-card2-border">
            <OilChangeCard key={`oil-${order.id}-${order.oil_change?.date || ""}`} order={order} shop={user?.shop} onSaved={refresh} />
            {order.status === "entregue" && <div className="mt-4"><SurveyButton order={order} /></div>}
          </div>
        )}

        {/* Checklist */}
        <div className="p-5 md:p-7 border-b border-card2-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold uppercase text-zinc-100">Checklist</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {order.checklist.map((item) => (
              <div key={item.key} data-testid={`checklist-item-${item.key}`} className="rounded-2xl border border-card2-border bg-appbg-soft/60 p-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">{item.label}</div>
                  <div className="text-[11px] font-mono-caps text-zinc-500">Item do checklist</div>
                </div>
                {readOnly ? (
                  <span data-testid={`checklist-value-${item.key}`} className={`inline-flex items-center justify-center px-3 py-1 rounded-full border text-xs font-bold uppercase ${CHECKLIST_META[item.value].classes}`}>
                    {CHECKLIST_META[item.value].label}
                  </span>
                ) : (
                  <div className="flex gap-1">
                    {CHECKLIST_VALUES.map((v) => {
                      const active = item.value === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          data-testid={`checklist-${item.key}-${v}`}
                          onClick={() => setChecklistValue(item.key, v)}
                          className={`px-2 py-1 text-[10px] font-bold uppercase rounded-md border transition-all ${
                            active ? CHECKLIST_META[v].classes + " scale-105" : "bg-zinc-900 text-zinc-500 border-zinc-800 hover:text-zinc-200"
                          }`}
                        >
                          {CHECKLIST_META[v].label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-5">
            <div className="text-[11px] font-mono-caps text-zinc-500 mb-2">Observações do mecânico</div>
            {readOnly ? (
              <div data-testid="mechanic-notes-view" className="whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-sm text-zinc-200 min-h-[60px]">
                {order.mechanic_notes || <span className="text-zinc-600">Sem observações.</span>}
              </div>
            ) : (
              <Textarea data-testid="mechanic-notes-input" value={order.mechanic_notes || ""}
                onChange={(e) => setOrder({ ...order, mechanic_notes: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[90px]"
                placeholder="Notas sobre o atendimento, peças trocadas..." />
            )}
          </div>
          <div className="mt-5">
            <div className="text-[11px] font-mono-caps text-zinc-500 mb-2">Laudo técnico <span className="normal-case text-zinc-600">(sai no PDF do cliente)</span></div>
            {readOnly ? (
              <div data-testid="technical-report-view" className="whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-sm text-zinc-200 min-h-[60px]">
                {order.technical_report || <span className="text-zinc-600">Sem laudo.</span>}
              </div>
            ) : (
              <Textarea data-testid="technical-report-input" value={order.technical_report || ""}
                onChange={(e) => setOrder({ ...order, technical_report: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 min-h-[80px]"
                placeholder="Diagnóstico encontrado, causa do defeito, recomendação..." />
            )}
          </div>
        </div>

        {/* Next revision */}
        <div className="p-5 md:p-7 border-b border-card2-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-amber-brand" />
              <h2 className="font-display text-base sm:text-xl font-bold uppercase text-zinc-100">Próxima revisão</h2>
            </div>
            {!readOnly && (nr.date || nr.km != null) && (
              <div className="flex flex-wrap items-center gap-2">
                {order.customer_phone && (
                  <a
                    data-testid="wa-revision-btn"
                    href={whatsappLinkRevision()}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir WhatsApp com lembrete pronto"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wide"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                )}
                <Button
                  data-testid="send-revision-reminder-btn"
                  size="sm"
                  variant="outline"
                  onClick={sendRevisionReminder}
                  disabled={sendingRev}
                  className="bg-zinc-900 border-amber-brand/60 text-amber-300 hover:bg-amber-brand/10"
                >
                  {sendingRev ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Send className="h-3.5 w-3.5 mr-1.5" /> Enviar por e-mail</>)}
                </Button>
              </div>
            )}
          </div>
          {readOnly ? (
            <div data-testid="revision-view" className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
              {nr.date || nr.km != null ? (
                <>
                  {nr.date && (
                    <div>
                      <div className="text-[11px] font-mono-caps text-zinc-500">Data</div>
                      <div className="font-display text-2xl font-black text-amber-brand" translate="no">
                        <span>{new Date(nr.date + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                      </div>
                    </div>
                  )}
                  {nr.km != null && (
                    <div>
                      <div className="text-[11px] font-mono-caps text-zinc-500">Quilometragem alvo</div>
                      <div className="font-display text-2xl font-black text-amber-brand" translate="no">
                        <span>{Number(nr.km).toLocaleString("pt-BR")} km</span>
                      </div>
                      {order.vehicle?.current_km != null && (
                        <div className="text-xs text-zinc-400 mt-1" translate="no">
                          <span>{`Hodômetro atual: ${Number(order.vehicle.current_km).toLocaleString("pt-BR")} km`}</span>
                          {Number(nr.km) - Number(order.vehicle.current_km) > 0 && (
                            <span>{` · faltam ${(Number(nr.km) - Number(order.vehicle.current_km)).toLocaleString("pt-BR")} km`}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {nr.note ? <div className="text-sm text-zinc-400">{nr.note}</div> : null}
                </>
              ) : (
                <div className="text-sm text-zinc-500">Nenhuma próxima revisão agendada.</div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Data (opcional)</div>
                <Input
                  data-testid="revision-date-input"
                  type="date"
                  value={nr.date || ""}
                  onChange={(e) => setOrder({ ...order, next_revision: { ...nr, date: e.target.value } })}
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Próxima revisão em (km)</div>
                <Input
                  data-testid="revision-km-input"
                  type="number"
                  min="0"
                  value={nr.km || ""}
                  onChange={(e) => setOrder({ ...order, next_revision: { ...nr, km: e.target.value ? Number(e.target.value) : null } })}
                  className="bg-zinc-900 border-zinc-800"
                  placeholder="30000"
                />
              </div>
              <div>
                <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Hodômetro atual (km)</div>
                <Input
                  data-testid="vehicle-km-input"
                  type="number"
                  min="0"
                  value={order.vehicle?.current_km ?? ""}
                  onChange={(e) => setOrder({ ...order, vehicle: { ...(order.vehicle || {}), current_km: e.target.value ? Number(e.target.value) : null } })}
                  className="bg-zinc-900 border-zinc-800"
                  placeholder="25500"
                />
              </div>
              <div className="sm:col-span-3">
                <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Observação</div>
                <Input
                  data-testid="revision-note-input"
                  value={nr.note || ""}
                  onChange={(e) => setOrder({ ...order, next_revision: { ...nr, note: e.target.value } })}
                  className="bg-zinc-900 border-zinc-800"
                  placeholder="Trocar correia dentada na próxima"
                />
              </div>
            </div>
          )}
        </div>

        {!readOnly && (
          <div data-testid="customer-rating-section" className="p-5 md:p-7 border-b border-card2-border">
            <div className="flex items-center gap-2 mb-3">
              <Star className="h-4 w-4 text-amber-brand" />
              <h2 className="font-display text-base sm:text-xl font-bold uppercase text-zinc-100">Avaliação do cliente</h2>
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`customer-rating-star-${n}`} aria-label={`${n} estrela(s)`} disabled={saving}
                  onClick={() => patch({ customer_rating: n }).catch(() => {})} className="p-0.5 disabled:opacity-60">
                  <Star className={`h-7 w-7 ${n <= (order.customer_rating || 0) ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                </button>
              ))}
              <span data-testid="customer-rating-value" className="ml-2 text-xs text-zinc-500">{order.customer_rating ? `${order.customer_rating} de 5` : "Sem avaliação"}</span>
            </div>
            <RatingComment order={order} saving={saving} onSave={(t) => patch({ customer_rating_comment: t }).then(() => toast.success("Comentário salvo")).catch(() => {})} />
          </div>
        )}

        {/* Intake photos (vehicle condition on arrival) */}
        <div className="p-4 md:p-7 border-b border-card2-border bg-appbg-soft/40">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h2 className="font-display text-base sm:text-xl font-bold uppercase text-zinc-100 flex flex-wrap items-center gap-x-2 gap-y-1">
                <ShieldCheck className="h-5 w-5 text-amber-brand shrink-0" />
                <span>Fotos de entrada</span>
                <span className="text-xs text-zinc-500 font-sans">({(order.intake_photos || []).length})</span>
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                Estado do carro na chegada — traseira, frente, laterais e interior. Visualizado pelo cliente desde o cadastro.
              </p>
            </div>
            {!readOnly && (user?.shop?.intake_whatsapp_enabled !== false) && (order.intake_photos || []).length > 0 && (
              <a data-testid="intake-whatsapp-btn" href={whatsappLinkIntake()} target="_blank" rel="noopener noreferrer"
                 className="self-start sm:self-auto inline-flex items-center gap-2 h-10 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold uppercase">
                <MessageCircle className="h-4 w-4" /> Enviar checklist pelo WhatsApp
              </a>
            )}
            {!readOnly && (
              <>
                <input ref={intakeRef} data-testid="intake-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={onUploadIntake} />
                <RetryUploads category="intake" />
                <Button
                  data-testid="intake-upload-btn"
                  onClick={() => intakeRef.current?.click()}
                  disabled={uploadingIntake}
                  className="self-start sm:self-auto bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase"
                >
                  {uploadingIntake ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Camera className="h-4 w-4 mr-2" /> Fotos de entrada</>)}
                </Button>
              </>
            )}
          </div>

          {(order.intake_photos || []).length === 0 ? (
            <div data-testid="intake-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-8 text-center">
              <ShieldCheck className="h-7 w-7 mx-auto text-zinc-700" />
              <p className="mt-2 text-sm text-zinc-500">
                {readOnly ? "A oficina ainda não registrou fotos de entrada." : "Registre pelo menos 5 fotos (traseira, frente, 2 laterais e 2 do interior)."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
              {order.intake_photos.map((p) => (
                <div key={p.id} data-testid={`intake-photo-${p.id}`} className="relative group">
                  <PhotoThumb photoId={p.id} onClick={setPreview} alt="Foto de entrada" />
                  {!readOnly && (
                    <button
                      data-testid={`intake-photo-remove-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 border border-zinc-700 text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      aria-label="Remover foto de entrada"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photos */}
        <div className="p-4 md:p-7">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <h2 className="font-display text-base sm:text-xl font-bold uppercase text-zinc-100 flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
              <Camera className="h-5 w-5 text-green-brand shrink-0 sm:hidden" />
              <span>Fotos do serviço</span>
              <span className="text-xs text-zinc-500 font-sans">({order.photos.length})</span>
            </h2>
            {!readOnly && (
              <>
                <input ref={fileRef} data-testid="photo-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
                <RetryUploads category="service" />
                <Button
                  data-testid="photo-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="self-start sm:self-auto bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Camera className="h-4 w-4 mr-2" /> Enviar fotos</>)}
                </Button>
              </>
            )}
          </div>

          {order.photos.length === 0 ? (
            <div data-testid="photos-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center">
              <Camera className="h-8 w-8 mx-auto text-zinc-700" />
              <p className="mt-2 text-sm text-zinc-500">
                {readOnly ? "Nenhuma foto adicionada ainda." : "Envie fotos do veículo e do serviço."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
              {order.photos.map((p) => (
                <div key={p.id} className="relative group">
                  <PhotoThumb photoId={p.id} onClick={setPreview} alt="Foto do serviço" />
                  {!readOnly && (
                    <button
                      data-testid={`photo-remove-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 border border-zinc-700 text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      aria-label="Remover foto"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Service videos */}
        <div className="p-4 md:p-7 border-t border-card2-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h2 className="font-display text-base sm:text-xl font-bold uppercase text-zinc-100 flex flex-wrap items-center gap-x-2 gap-y-1">
                <Video className="h-5 w-5 text-green-neon shrink-0" />
                <span>Vídeos do serviço</span>
                <span className="text-xs text-zinc-500 font-sans">({(order.service_videos || []).length}/3)</span>
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                Até 3 vídeos por ordem, no máximo 30 segundos cada. Os vídeos ficam disponíveis por 90 dias após a entrega.
              </p>
            </div>
            {!readOnly && (
              <>
                <input ref={videoRef} data-testid="video-upload-input" type="file" accept="video/mp4,video/quicktime,.mp4,.mov" capture="environment" className="hidden" onChange={onUploadVideo} />
                <Button
                  data-testid="video-upload-btn"
                  onClick={() => videoRef.current?.click()}
                  disabled={uploadingVideo || (order.service_videos || []).length >= 3}
                  className="self-start sm:self-auto bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
                >
                  {uploadingVideo ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Video className="h-4 w-4 mr-2" /> Enviar vídeo</>)}
                </Button>
              </>
            )}
          </div>

          {uploadingVideo && (
            <div data-testid="video-progress" className="mb-3">
              <div className="h-2 bg-zinc-900 rounded-full overflow-hidden border border-card2-border">
                <div className="h-full bg-green-neon transition-all" style={{ width: `${videoProgress}%` }} />
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">{videoProgress}%</div>
            </div>
          )}

          {(order.service_videos || []).length === 0 ? (
            <div data-testid="videos-empty" className="rounded-xl border border-dashed border-card2-border bg-appbg-soft/40 p-8 text-center">
              <Video className="h-7 w-7 mx-auto text-zinc-700" />
              <p className="mt-2 text-sm text-zinc-500">
                {readOnly ? "Nenhum vídeo foi enviado pela oficina." : "Grave um vídeo rápido do serviço (até 30s)."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {order.service_videos.map((v) => (
                <VideoPlayer key={v.id} video={v} onRemove={readOnly ? null : () => removeVideo(v.id)} />
              ))}
            </div>
          )}
        </div>

        {/* Exit photos (vehicle condition on delivery) */}
        <div className="p-4 md:p-7 border-t border-card2-border bg-appbg-soft/40">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h2 className="font-display text-base sm:text-xl font-bold uppercase text-zinc-100 flex flex-wrap items-center gap-x-2 gap-y-1">
                <PackageCheck className="h-5 w-5 text-green-brand shrink-0" />
                <span>Fotos de saída</span>
                <span className="text-xs text-zinc-500 font-sans">({(order.exit_photos || []).length})</span>
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                Registre o estado do carro na entrega — as mesmas 5 vistas (traseira, frente, laterais e interior). Fica disponível para o cliente após a conclusão.
              </p>
            </div>
            {!readOnly && (
              <>
                <input ref={exitRef} data-testid="exit-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={onUploadExit} />
                <RetryUploads category="exit" />
                <Button
                  data-testid="exit-upload-btn"
                  onClick={() => exitRef.current?.click()}
                  disabled={uploadingExit}
                  className="self-start sm:self-auto bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
                >
                  {uploadingExit ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Camera className="h-4 w-4 mr-2" /> Fotos de saída</>)}
                </Button>
              </>
            )}
          </div>

          {(order.exit_photos || []).length === 0 ? (
            <div data-testid="exit-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-8 text-center">
              <PackageCheck className="h-7 w-7 mx-auto text-zinc-700" />
              <p className="mt-2 text-sm text-zinc-500">
                {readOnly ? "A oficina ainda não registrou fotos de saída." : "Registre até 5 fotos (traseira, frente, 2 laterais e 2 do interior) na entrega."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
              {order.exit_photos.map((p) => (
                <div key={p.id} data-testid={`exit-photo-${p.id}`} className="relative group">
                  <PhotoThumb photoId={p.id} onClick={setPreview} alt="Foto de saída" />
                  {!readOnly && (
                    <button
                      data-testid={`exit-photo-remove-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 border border-zinc-700 text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      aria-label="Remover foto de saída"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {!readOnly && (
          <div className="sticky bottom-16 md:bottom-0 z-20 bg-appbg/95 backdrop-blur border-t border-card2-border p-4 md:p-5 flex items-center justify-between gap-3">
            <div className="text-xs text-zinc-400 hidden sm:block">
              Salva checklist, observações e próxima revisão. Ao definir ou mudar a data da próxima revisão, o cliente recebe um e-mail de aviso.
            </div>
            <Button
              data-testid="save-all-btn"
              onClick={saveAll}
              disabled={savingAll}
              className="h-12 px-6 rounded-2xl bg-gradient-to-b from-green-neon to-green-brandDark text-zinc-950 font-bold uppercase text-sm tracking-wider shadow-[0_8px_28px_-10px_rgba(74,222,128,0.55)] hover:brightness-110"
            >
              {savingAll ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Save className="h-5 w-5 mr-2" /> Salvar alterações</>)}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview("")}>
        <DialogContent className="bg-black border-zinc-800 max-w-3xl p-2">
          <DialogTitle className="sr-only">Visualizar foto</DialogTitle>
          {preview && <img src={preview} alt="Foto ampliada" className="w-full h-auto rounded-md" />}
        </DialogContent>
      </Dialog>
      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        total={order?.budget?.decision === "aprovado" ? order?.budget?.total : null}
        initial={order?.payment}
        onConfirm={async (payload) => {
          try {
            const { data } = await api.post(`/orders/${id}/payment`, payload);
            setOrder((prev) => ({ ...(prev || data), payment: data.payment }));
            if (pendingStatus) await patch({ status: pendingStatus });
            toast.success("Pagamento registrado");
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setPaymentOpen(false); setPendingStatus(null); }
        }}
        onSkip={() => {
          setPaymentOpen(false);
          const s = pendingStatus; setPendingStatus(null);
          if (s) patch({ status: s });
        }}
      />
    </div>
  );
}
